/**
 * HYPSEYA Analytics — PostHog wrapper
 *
 * Single source of truth for all product events.
 * Import `track` anywhere and call it — PostHog handles batching + retry.
 *
 * Setup:
 *   1. Create free account at https://app.posthog.com
 *   2. Copy your Project API key
 *   3. Add to .env:  REACT_APP_POSTHOG_KEY=phc_xxxx
 *   4. Add to .env:  REACT_APP_POSTHOG_HOST=https://eu.i.posthog.com  (EU region)
 *
 * All events follow the format:  category_action
 * e.g. session_completed, voice_cloned, meditation_played
 */

import posthog from 'posthog-js';

const KEY  = process.env.REACT_APP_POSTHOG_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://eu.i.posthog.com';

let initialised = false;

export function initAnalytics() {
  if (!KEY || initialised) return;
  posthog.init(KEY, {
    api_host:             HOST,
    capture_pageview:     true,   // auto page views
    capture_pageleave:    true,
    autocapture:          false,  // manual events only — no noise
    persistence:          'localStorage',
    session_recording: {
      maskAllInputs: true,        // never record text typed by user
      maskTextSelector: '*',      // never record journal/script content
    },
    loaded: (ph) => {
      // Opt out of PostHog's own telemetry
      if (process.env.NODE_ENV !== 'production') ph.opt_out_capturing();
    },
  });
  initialised = true;
}

// ── Identify user (call after login) ─────────────────────────────────────────
export function identifyUser(userId, properties = {}) {
  if (!initialised) return;
  posthog.identify(userId, {
    name:          properties.name,
    focus_area:    properties.focus_area,
    onboarded_at:  properties.created_at,
    has_voice:     properties.cloned_voice_status === 'ready',
    platform:      properties.platform || 'web',
  });
}

// ── Reset on logout ───────────────────────────────────────────────────────────
export function resetAnalytics() {
  if (!initialised) return;
  posthog.reset();
}

// ── Core track function ───────────────────────────────────────────────────────
export function track(event, properties = {}) {
  if (!initialised) return;
  posthog.capture(event, {
    ...properties,
    app_version: process.env.REACT_APP_VERSION || '1.0.0',
  });
}

// ── Predefined events (keeps naming consistent across the app) ────────────────

// Onboarding
export const Analytics = {
  // Onboarding
  onboardingStarted:    (step)           => track('onboarding_started',      { step }),
  onboardingCompleted:  (durationSec)    => track('onboarding_completed',    { duration_sec: durationSec }),
  onboardingDropped:    (step)           => track('onboarding_dropped',      { dropped_at_step: step }),

  // Daily Loop
  sessionStarted:       (focusArea)      => track('session_started',         { focus_area: focusArea }),
  sessionCompleted:     (props)          => track('session_completed',        props),
  sessionAbandoned:     (step, focusArea)=> track('session_abandoned',       { step, focus_area: focusArea }),

  // ERI
  eriViewed:            (score)          => track('eri_viewed',              { eri_score: score }),
  eriImproved:          (delta)          => track('eri_improved',            { delta }),

  // Voice
  voiceCloneStarted:    ()               => track('voice_clone_started'),
  voiceCloneCompleted:  ()               => track('voice_clone_completed'),
  voiceCloneFailed:     (reason)         => track('voice_clone_failed',      { reason }),

  // Meditations
  meditationCreated:    (focusArea)      => track('meditation_created',      { focus_area: focusArea }),
  meditationPlayed:     (id, duration)   => track('meditation_played',       { meditation_id: id, duration_sec: duration }),
  meditationCompleted:  (id)             => track('meditation_completed',    { meditation_id: id }),

  // Moment of Need
  momentStarted:        ()               => track('moment_started'),
  momentCompleted:      ()               => track('moment_completed'),
  momentAbandoned:      ()               => track('moment_abandoned'),

  // Insights
  insightsViewed:       ()               => track('insights_viewed'),
  weeklyReportGenerated:()               => track('weekly_report_generated'),
  clinicalReportViewed: ()               => track('clinical_report_viewed'),
  proofOfChangeViewed:  ()               => track('proof_of_change_viewed'),

  // Health data
  healthDataConnected:  (source)         => track('health_data_connected',   { source }),
  biometricSynced:      (score)          => track('biometric_synced',        { biometric_score: score }),

  // Assessment
  assessmentCompleted:  (type, score)    => track('assessment_completed',    { type, score }),

  // Journal
  journalEntryCreated:  (wordCount)      => track('journal_entry_created',   { word_count: wordCount }),

  // Retention signals
  appOpened:            ()               => track('app_opened'),
  pushNotificationTapped: (source)       => track('push_notification_tapped',{ source }),
};

export default Analytics;
